/**
 * Patient Data Mapping Utility
 * Transforms ApplicationContext data into PatientDataPayload format
 */

import type { Address, AppointmentDetails, SharedData } from "@src/context/ApplicationContext";
import type { UTMParams } from "@src/context/UTMContext";
import type { ProviderDepartment } from "@src/interfaces/provider.interface";
import type { PatientDataPayload } from "@src/services/patient-data.service";
import { ISO_DATE_ONLY_PATTERN, toAppointmentIsoDateTime } from "@src/utils/date-utils";
import logger from "@src/utils/logger";
import { toFiniteNumber } from "@src/utils/number-utils";
import { isUnder18AgeRange } from "@src/utils/scheduling-metadata";

/**
 * Converts a date string to YYYY-MM-DD format
 * Handles various input formats (ISO, timestamps, Date objects, etc.)
 */
const formatDateToISO = (date: string | null | undefined): string | null => {
  if (!date) return null;

  try {
    // If it's already in YYYY-MM-DD format, return as is
    if (ISO_DATE_ONLY_PATTERN.test(date)) {
      return date;
    }

    // Parse and format to YYYY-MM-DD
    const dateObj = new Date(date);
    if (Number.isNaN(dateObj.getTime())) return null;

    return dateObj.toISOString().split("T")[0];
  } catch {
    return null;
  }
};

/**
 * Maps relationship type to Athena relationshiptoinsuredid value
 * @param relationship - Relationship type from frontend (parent, spouse, child, other)
 * @returns Athena relationship ID (1=parent, 2=spouse, 3=child, 4=other)
 */
const mapRelationshipToInsuredId = (relationship: string): number | null => {
  if (!relationship) return null; // Default to null

  const relationshipLower = relationship.toLowerCase();
  switch (relationshipLower) {
    case "spouse":
      return 2;
    case "child":
      return 3;
    case "other":
      return 4;
    default:
      return null;
  }
};

const resolveInsurancePackageId = (insurancePolicy: unknown): string | number | undefined => {
  if (typeof insurancePolicy === "string") {
    const trimmed = insurancePolicy.trim();
    return trimmed ? trimmed : undefined;
  }

  if (typeof insurancePolicy === "number") {
    return toFiniteNumber(insurancePolicy);
  }

  if (insurancePolicy && typeof insurancePolicy === "object" && "id" in insurancePolicy) {
    return resolveInsurancePackageId((insurancePolicy as { id?: unknown }).id);
  }

  return undefined;
};

// Reuses field types from SharedData (address.context_id and appointmentDetails.apptLocation.department_id
// are numbers there) so this shape cannot silently drift from the production context contract.
type PatientContextData = Pick<
  SharedData,
  | "ageRange"
  | "careFormat"
  | "dob"
  | "isPrimaryPolicyHolder"
  | "medMgmtInd"
  | "memberId"
  | "paymentMethod"
  | "policyholderDOB"
  | "policyholderFirstName"
  | "policyholderLastName"
  | "relationshipToPolicyholder"
> & {
  address?: Partial<Pick<Address, "context_id">>;
  appointmentDetails?: Pick<AppointmentDetails, "apptDate" | "apptTime" | "providerId"> & {
    apptLocation?: Pick<ProviderDepartment, "department_id">;
  };
  insurancePolicy?: unknown;
  memberFirstName?: string;
  memberLastName?: string;
};

const mapPatientInformation = (
  contextData: PatientContextData,
  payload: Partial<PatientDataPayload>,
): string | null => {
  if (contextData.memberFirstName) payload.patient_first_name = contextData.memberFirstName;
  if (contextData.memberLastName) payload.patient_last_name = contextData.memberLastName;

  const formattedDob = formatDateToISO(contextData.dob);
  if (formattedDob) payload.patient_dob = formattedDob;
  if (contextData.careFormat) payload.service_type = contextData.careFormat;

  return formattedDob;
};

const mapAppointmentInformation = (
  contextData: PatientContextData,
  payload: Partial<PatientDataPayload>,
): void => {
  const appointmentDateTime = toAppointmentIsoDateTime(
    contextData.appointmentDetails?.apptDate,
    contextData.appointmentDetails?.apptTime,
  );
  if (appointmentDateTime) payload.appointment_datetime = appointmentDateTime;
  if (contextData.appointmentDetails?.providerId) {
    payload.provider_id = parseInt(contextData.appointmentDetails.providerId, 10);
  }
  if (contextData.appointmentDetails?.apptLocation?.department_id) {
    payload.provider_clinic = contextData.appointmentDetails.apptLocation.department_id;
  }
};

const mapInsuranceInformation = (
  contextData: PatientContextData,
  payload: Partial<PatientDataPayload>,
  formattedDob: string | null,
): void => {
  if (contextData.paymentMethod === "self_pay") return;

  if (contextData.memberId) payload.insurance_member_id = contextData.memberId;
  const insurancePackageId = resolveInsurancePackageId(contextData.insurancePolicy);
  if (insurancePackageId !== undefined) {
    payload.insurance_package_id = insurancePackageId as PatientDataPayload["insurance_package_id"];
  }

  if (contextData.isPrimaryPolicyHolder) {
    if (contextData.memberFirstName) {
      payload.insurance_policy_subscriber_first_name = contextData.memberFirstName;
    }
    if (contextData.memberLastName) {
      payload.insurance_policy_subscriber_last_name = contextData.memberLastName;
    }
    if (formattedDob) payload.insurance_policy_subscriber_dob = formattedDob;
    return;
  }

  if (contextData.policyholderFirstName) {
    payload.insurance_policy_subscriber_first_name = contextData.policyholderFirstName;
  }
  if (contextData.policyholderLastName) {
    payload.insurance_policy_subscriber_last_name = contextData.policyholderLastName;
  }
  if (contextData.relationshipToPolicyholder) {
    payload.insurance_relationship_to_policy_holder = mapRelationshipToInsuredId(
      contextData.relationshipToPolicyholder,
    );
  }
  const formattedPolicyholderDob = formatDateToISO(contextData.policyholderDOB);
  if (formattedPolicyholderDob) {
    payload.insurance_policy_subscriber_dob = formattedPolicyholderDob;
  }
};

const mapUtmParameters = (
  utmTags: UTMParams | undefined,
  payload: Partial<PatientDataPayload>,
): void => {
  payload.utm_source = utmTags?.utm_source ?? null;
  payload.utm_medium = utmTags?.utm_medium ?? null;
  payload.utm_campaign = utmTags?.utm_campaign ?? null;
  payload.utm_content = utmTags?.utm_content ?? null;
  payload.utm_theme = utmTags?.utm_theme ?? null;
};

const mapPracticeInformation = (
  contextData: PatientContextData,
  payload: Partial<PatientDataPayload>,
): void => {
  if (contextData.address?.context_id) {
    payload.practice_id = contextData.address.context_id;
    logger.info({
      text: `Setting practice_id from address context_id:`,
      value: contextData.address.context_id,
      location: "patient-data-mapping.mapContextToPatientData",
    });
    return;
  }

  logger.warn({
    text: "No practice_id found - address.context_id not available",
    location: "patient-data-mapping.mapContextToPatientData",
  });
};

const mapAgeRangeInformation = (
  contextData: PatientContextData,
  payload: Partial<PatientDataPayload>,
): void => {
  const ageRange = contextData.ageRange?.trim();
  if (!ageRange) return;

  const isMinor = isUnder18AgeRange(ageRange);
  payload.attestation_minor = isMinor;
  payload.attestation_adult = !isMinor;
  payload.signup_as_minor = isMinor;
};

/**
 * Maps ApplicationContext data to PatientDataPayload
 * Gathers all required fields from context and formats them appropriately
 * Only includes fields that have valid values, except UTM fields which are sent as null when absent.
 *
 * @param contextData - The SharedData from ApplicationContext
 * @param utmTags - UTM parameters from ApplicationContext
 * @returns PatientDataPayload object ready for API submission (only with populated fields)
 */
export const mapContextToPatientData = (
  contextData: PatientContextData,
  utmTags?: UTMParams,
): Partial<PatientDataPayload> => {
  const payload: Partial<PatientDataPayload> = {};

  const formattedDob = mapPatientInformation(contextData, payload);
  mapAppointmentInformation(contextData, payload);
  mapInsuranceInformation(contextData, payload, formattedDob);
  mapUtmParameters(utmTags, payload);
  mapPracticeInformation(contextData, payload);
  mapAgeRangeInformation(contextData, payload);

  // Med management indicator
  if (contextData.medMgmtInd) {
    payload.med_mgmt_ind = 1;
  }

  return payload;
};

export default mapContextToPatientData;
